import {
  Button,
  CloseIcon,
  CopyIcon,
  Header,
  IconButton,
  Paragraph,
  ShareIcon,
  TextInput,
  notification,
} from "@datagame/ui";
import { Dialog } from "@headlessui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames/bind";

import { Section } from "../../../project/ProjectView/ProjectSettings/Steps/Common";
import ShareImage from "../../../../assets/images/share.svg";
import { useUser } from "../../../../hooks/user";
import { Game, GameKind, PlanName, Project } from "../../../../gql/graphql";

import styles from "./ShareProjectModal.module.scss";
import { SelectInput } from "../../ui/SelectInput";
import { useQuery } from "@apollo/client";
import { GAME_ALL_IDS } from "../../../../graphql/queries/GameQueries";
import { SelectOption } from "../../ui/SelectInput/SelectInput";
import { isQuickPickGame } from "../../../../graphql/helpers/isQuickPickGame";

const RESPONDER_URL = import.meta.env.VITE_RESPONDER_URL;

interface ShareProjectModalProps {
  project?: Project;
  onClose: () => void;
}

const SHARE_OPTIONS = {
  link: "link",
  alchemer: "alchemer",
  leaderboard: "leaderboard",
};

const cx = classNames.bind(styles);

const AlchemerSection = ({ projectId }: { projectId?: string }) => {
  const code = `window.__DATAGAME_CONFIGURATION = {
  projectId: "${projectId}",
  provider: "alchemer",
};
const host = document.createElement("div");
host.id = "datagame-embedded-root";
const el = document.getElementsByClassName("sg-type-textbox");
el[0].appendChild(host);
const shadowRoot = host.attachShadow({ mode: "open" });
window.__DATAGAME_SHADOW_ROOT = shadowRoot;
document.getElementsByClassName("sg-question-title").display = "none";
document.getElementsByClassName("sg-input-text").display = "none";
const app = document.createElement("script");
app.src = "${RESPONDER_URL}/assets/index.js";
app.type = "module";
const css = document.createElement("link");
css.rel = "stylesheet";
css.href = "${RESPONDER_URL}/assets/index.css";
shadowRoot.appendChild(css);
el[0].appendChild(app);`;

  const { t } = useTranslation();

  const handleCopyCode = () => {
    navigator.clipboard.writeText(code);
    notification.info({
      title: t("projects:shareModal.copyCode.title"),
      description: t("projects:shareModal.copyCode.description"),
    });
  };
  return (
    <Section
      className={styles.section}
      headerClassName={styles.shareHeader}
      title={t("projects:shareModal.shareAlchemer")}
    >
      <ol className={styles.stepsList}>
        <li>
          <Paragraph size="xs" className={styles.step}>
            {t("projects:shareModal.alchemer.step1")}
          </Paragraph>
        </li>
        <li>
          <Paragraph size="xs" className={styles.step}>
            {t("projects:shareModal.alchemer.step2")}
          </Paragraph>
        </li>
        <li>
          <Paragraph size="xs" className={styles.step}>
            {t("projects:shareModal.alchemer.step3")}
          </Paragraph>
        </li>
      </ol>

      <div className={styles.codeSection}>
        <pre>
          <code>
            <Paragraph size="xs" className={styles.code}>
              {code}
            </Paragraph>
          </code>
        </pre>
      </div>

      <Button icon={<CopyIcon />} iconSide="left" className={styles.copyButton} onClick={handleCopyCode}>
        {t("projects:shareModal.alchemer.copyCode")}
      </Button>
    </Section>
  );
};

const ProjectLeaderboardSection = ({ project }: { project?: Project }) => {
  const { t } = useTranslation();

  const code = `<div style="display: flex;">
  <iframe style="border: 0; width: 100%; height: 600px;" src="${RESPONDER_URL}/${project?.id}/leaderboard"></iframe>
</div>`.trim();

  const handleCopyCode = () => {
    navigator.clipboard.writeText(code);
    notification.info({
      title: t("projects:shareModal.copyCode.title"),
      description: t("projects:shareModal.copyCode.description"),
    });
  };

  return (
    <Section
      className={styles.section}
      headerClassName={styles.shareHeader}
      title={t("projects:shareModal.shareLeaderboard")}
    >
      <ol className={styles.stepsList}>
        <li>
          <Paragraph size="xs" className={styles.step}>
            {t("projects:shareModal.leaderboard.projectLeaderboard")}
          </Paragraph>
        </li>
      </ol>

      <div className={styles.leaderboardCodeSection}>
        <pre>
          <code>
            <Paragraph size="xs" className={styles.code}>
              {code}
            </Paragraph>
          </code>
        </pre>
      </div>

      <Button
        icon={<CopyIcon />}
        iconSide="left"
        className={styles.copyProjectLeaderboardButton}
        onClick={handleCopyCode}
      >
        {t("projects:shareModal.leaderboard.copyCode")}
      </Button>
    </Section>
  );
};

const LeaderboardSection = ({ project }: { project?: Project }) => {
  const [selectedGame, setSelectedGame] = useState<SelectOption>({ label: "", value: "" });
  const { data: { gameAll: games } = { gameAll: [] } } = useQuery(GAME_ALL_IDS, {
    variables: { projectId: project?.id },
    skip: !project?.id,
  });

  const code = `
<div style="display: flex;">
  <iframe style="border: 0; width: 100%; height: 600px;" src="${RESPONDER_URL}/${project?.id}/${selectedGame?.value}/leaderboard"></iframe>
</div>`.trim();

  const { t } = useTranslation();

  const handleCopyCode = () => {
    navigator.clipboard.writeText(code);
    notification.info({
      title: t("projects:shareModal.copyCode.title"),
      description: t("projects:shareModal.copyCode.description"),
    });
  };

  const gamesList = games
    ?.filter(
      (game: Game) =>
        [GameKind.Wordopolis, GameKind.Swiper, GameKind.Wordopolis, GameKind.Trivia, GameKind.Wordle].includes(
          game.kind,
        ) || isQuickPickGame(game),
    )
    .map((game: Game) => ({ label: game.name, value: game.id }));

  return (
    <>
      <Section
        className={styles.selectGameSection}
        title={t("projects:shareModal.selectGame")}
        headerContainerClassName={styles.selectGame}
      >
        <SelectInput options={gamesList} selected={selectedGame} onChange={setSelectedGame} />
      </Section>

      <Section
        className={styles.section}
        headerClassName={styles.shareHeader}
        title={t("projects:shareModal.shareLeaderboard")}
      >
        <ol className={styles.stepsList}>
          <li>
            <Paragraph size="xs" className={styles.step}>
              {t("projects:shareModal.leaderboard.step1")}
            </Paragraph>
          </li>
          <li>
            <Paragraph size="xs" className={styles.step}>
              {t("projects:shareModal.leaderboard.step2")}
            </Paragraph>
          </li>
        </ol>

        <div className={styles.leaderboardCodeSection}>
          <pre>
            <code>
              <Paragraph size="xs" className={styles.code}>
                {code}
              </Paragraph>
            </code>
          </pre>
        </div>

        <Button
          icon={<CopyIcon />}
          iconSide="left"
          className={styles.copyButton}
          onClick={handleCopyCode}
          disabled={!selectedGame.value}
        >
          {t("projects:shareModal.leaderboard.copyCode")}
        </Button>
      </Section>
    </>
  );
};

export const ShareProjectModal = ({ project, onClose }: ShareProjectModalProps) => {
  const { t } = useTranslation();
  const { user } = useUser();

  const canUseAlchemer = useMemo(() => {
    return user?.organization.subscription?.planName !== PlanName.Basic;
  }, [user?.organization.subscription?.planName]);

  const [activeOption, setActiveOption] = useState(SHARE_OPTIONS.link);

  const copyLink = useMemo(() => {
    return `${RESPONDER_URL}/${project?.id}`;
  }, [project?.id]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(copyLink);
    notification.info({
      title: t("projects:shareModal.copyNotification.title"),
      description: t("projects:shareModal.copyNotification.description"),
    });
  };

  const renderContent = () => {
    if (activeOption === SHARE_OPTIONS.link) {
      return (
        <>
          <img src={ShareImage} alt="share"></img>

          <Section
            className={styles.section}
            headerClassName={styles.shareHeader}
            title={t("projects:shareModal.shareByLink")}
          >
            <div className={styles.sectionContent}>
              <TextInput icon={<ShareIcon />} readOnly value={copyLink} size="small" />
              <Button
                icon={<ShareIcon />}
                iconSide="left"
                onClick={handleCopyLink}
                className={cx("copyButton", "desktop")}
              >
                {t("projects:shareModal.copyLink")}
              </Button>
            </div>
            <Paragraph size="xs" className={styles.tip}>
              {t("projects:shareModal.copyTip")}
            </Paragraph>

            <Button
              icon={<ShareIcon />}
              iconSide="left"
              onClick={handleCopyLink}
              className={cx("copyButton", "mobile")}
            >
              {t("projects:shareModal.copyLink")}
            </Button>
          </Section>
        </>
      );
    }

    if (activeOption === SHARE_OPTIONS.alchemer) {
      return <AlchemerSection projectId={project?.id} />;
    }

    if (activeOption === SHARE_OPTIONS.leaderboard && project?.showLeaderboard) {
      return <ProjectLeaderboardSection project={project} />;
    }

    if (activeOption === SHARE_OPTIONS.leaderboard) {
      return <LeaderboardSection project={project} />;
    }
  };

  return (
    <Dialog open={true} onClose={onClose}>
      <div className={styles.overlay} />
      <Dialog.Panel>
        <div className={styles.modal}>
          <div className={styles.headerWrapper}>
            <div className={styles.header}>
              <Header variant="h6">{t("projects:shareModal.shareProject")}</Header>
              <IconButton size="small" onClick={onClose} className={styles.close} icon={<CloseIcon />} />
            </div>
            <div className={styles.options}>
              <div
                className={cx("option", { active: activeOption === SHARE_OPTIONS.link })}
                onClick={() => setActiveOption(SHARE_OPTIONS.link)}
                tabIndex={0}
                onKeyDown={() => setActiveOption(SHARE_OPTIONS.link)}
                role="button"
              >
                <Paragraph size="xs" weight="font-bold" className={styles.optionTitle}>
                  {t("projects:shareModal.shareByLink")}
                </Paragraph>
              </div>
              {canUseAlchemer && (
                <>
                  <div
                    className={cx("option", { active: activeOption === SHARE_OPTIONS.alchemer })}
                    onClick={() => setActiveOption(SHARE_OPTIONS.alchemer)}
                    tabIndex={0}
                    onKeyDown={() => setActiveOption(SHARE_OPTIONS.link)}
                    role="button"
                  >
                    <Paragraph size="xs" weight="font-bold" className={styles.optionTitle}>
                      {t("projects:shareModal.shareAlchemer")}
                    </Paragraph>
                  </div>
                  <div
                    className={cx("option", { active: activeOption === SHARE_OPTIONS.leaderboard })}
                    onClick={() => setActiveOption(SHARE_OPTIONS.leaderboard)}
                    tabIndex={0}
                    onKeyDown={() => setActiveOption(SHARE_OPTIONS.link)}
                    role="button"
                  >
                    <Paragraph size="xs" weight="font-bold" className={styles.optionTitle}>
                      {t("projects:shareModal.shareLeaderboard")}
                    </Paragraph>
                  </div>
                </>
              )}
            </div>
          </div>

          {renderContent()}
        </div>
      </Dialog.Panel>
    </Dialog>
  );
};
